/**
 * Work Clock — Live Overview (admin) / My Clock (developer).
 *
 * Fully redesigned with the self-contained dashboard-pages design system.
 * Admin: team pulse, live member status, pomodoro, commits.
 * Developer: session timer, earnings, focus tracker, personal work log.
 */

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  Activity,
  ArrowRight,
  Clock3,
  Flame,
  GitCommitHorizontal,
  Pause,
  Play,
  RotateCcw,
  Timer,
  Users,
  Zap,
} from 'lucide-react';
import { useTeamDashboard } from '../TeamDashboardContext';
import './dashboard-pages.css';

const T = {
  accent: '#6e6bff',
  accent2: '#a855f7',
  cyan: '#22d3ee',
  green: '#34d399',
  amber: '#fbbf24',
  red: '#fb4d6d',
};

const FONT_ID = 'tdp-fonts';
function ensureFonts() {
  if (document.getElementById(FONT_ID)) return;
  const link = document.createElement('link');
  link.id = FONT_ID;
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

const Card: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className={`tdp-card ${className}`}
  >
    {children}
  </motion.section>
);

const SectionTitle: React.FC<{ children: React.ReactNode; right?: React.ReactNode }> = ({ children, right }) => (
  <div className="tdp-section-title">
    <span>{children}</span>
    {right}
  </div>
);

const DEMO_COMMITS = [
  { initials: 'AS', msg: 'fix: remove hardcoded JWT fallback', repo: 'scrudix-backend', hash: 'c4d2b9e', delta: '+8 -4', time: '10:12 AM', color: T.accent },
  { initials: 'AS', msg: 'fix: restore rate limiting on /auth/login', repo: 'scrudix-backend', hash: 'f6c4e8d', delta: '+22', time: '11:05 AM', color: T.accent2 },
  { initials: 'AS', msg: 'feat: wire live budget telemetry', repo: 'team-dashboard', hash: '9a1f3c2', delta: '+46 -11', time: '11:48 AM', color: T.cyan },
];

const fmtClock = (s: number) => {
  const h = Math.floor(s / 3600).toString().padStart(2, '0');
  const m = Math.floor((s % 3600) / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${h}:${m}:${ss}`;
};

export default function WorkClockPage() {
  const { showToast, setActivePage, isReal, isManager, currentMemberName, currentMemberInitials, approvedMembers, budget, theme, projects } = useTeamDashboard();

  const [working, setWorking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* pomodoro */
  const [pomoActive, setPomoActive] = useState(false);
  const [pomoTime, setPomoTime] = useState(1500);
  const [pomoMode, setPomoMode] = useState<'focus' | 'break'>('focus');
  const [pomoCycles, setPomoCycles] = useState(0);
  const pomoTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    ensureFonts();
  }, []);

  useEffect(() => {
    if (pomoActive) {
      pomoTimerRef.current = setInterval(() => {
        setPomoTime((t) => {
          if (t <= 1) {
            if (pomoTimerRef.current) clearInterval(pomoTimerRef.current);
            setPomoActive(false);
            if (pomoMode === 'focus') {
              setPomoCycles((c) => c + 1);
              showToast('Focus session complete', 'Excellent work. Take a 5-minute break.', 'success');
              setPomoMode('break');
              return 300;
            }
            showToast('Break over', 'Ready for another focus interval?', 'info');
            setPomoMode('focus');
            return 1500;
          }
          return t - 1;
        });
      }, 1000);
    } else if (pomoTimerRef.current) {
      clearInterval(pomoTimerRef.current);
    }
    return () => {
      if (pomoTimerRef.current) clearInterval(pomoTimerRef.current);
    };
  }, [pomoActive, pomoMode, showToast]);

  useEffect(() => {
    if (working) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [working]);

  const myRate = isReal ? Number(approvedMembers.find((m) => m.initials === currentMemberInitials)?.rate || 500) : 500;
  const earned = ((elapsed / 3600) * myRate).toFixed(2);
  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short' });
  const firstName = (currentMemberName || 'there').split(' ')[0];
  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening';
  const workingCount = isReal ? approvedMembers.filter((m) => m.status === 'working').length : 3;

  const pomoClock = `${Math.floor(pomoTime / 60).toString().padStart(2, '0')}:${(pomoTime % 60).toString().padStart(2, '0')}`;

  const isDark = theme === 'dark';

  const PomodoroCard = (
    <Card delay={0.18} className="tdp-pomo">
      <div style={{ flex: 1, minWidth: 240 }}>
        <div className="tdp-eyebrow" style={{ marginBottom: 6 }}>
          <Timer size={11} />
          Pomodoro deep work flow
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tdp-text)' }}>
          Protect your quiet coding hours.
        </div>
        <p style={{ margin: '6px 0 12px', fontSize: 11.5, lineHeight: 1.6, color: 'var(--tdp-dim)' }}>
          Start a 25-minute focus interval — Scrudix records your deep work achievements automatically.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button
            className={`tdp-btn ${pomoActive ? 'tdp-btn-danger' : 'tdp-btn-primary'}`}
            onClick={() => setPomoActive(!pomoActive)}
          >
            {pomoActive ? <Pause size={13} /> : <Play size={13} />}
            {pomoActive ? 'Pause focus' : 'Start deep focus'}
          </button>
          <button
            className="tdp-btn tdp-btn-ghost"
            onClick={() => {
              setPomoActive(false);
              setPomoTime(pomoMode === 'focus' ? 1500 : 300);
            }}
          >
            <RotateCcw size={12} /> Reset
          </button>
          <div className="tdp-toggle">
            <button
              className={pomoMode === 'focus' ? 'tdp-on' : ''}
              onClick={() => {
                if (!pomoActive) {
                  setPomoMode('focus');
                  setPomoTime(1500);
                }
              }}
            >
              25m focus
            </button>
            <button
              className={pomoMode === 'break' ? 'tdp-on' : ''}
              onClick={() => {
                if (!pomoActive) {
                  setPomoMode('break');
                  setPomoTime(300);
                }
              }}
            >
              5m break
            </button>
          </div>
        </div>
      </div>
      <div className="tdp-pomo-clock">
        <div className="tdp-pomo-time">{pomoClock}</div>
        <div className="tdp-kpi-label" style={{ marginTop: 6 }}>
          {pomoMode === 'focus' ? 'focus interval' : 'short break'}
        </div>
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--tdp-dim)' }}>Flow sessions today</span>
          <span className="tdp-pill" style={{ background: 'var(--tdp-hover)', color: 'var(--tdp-accent)' }}>
            {pomoCycles}
          </span>
        </div>
      </div>
    </Card>
  );

  const CommitsCard = ({ title, delay }: { title: string; delay: number }) => (
    <Card delay={delay}>
      <div className="tdp-section-title" style={{ padding: '14px 16px 10px', margin: 0, borderBottom: '1px solid var(--tdp-border)' }}>
        <span>{title}</span>
        <GitCommitHorizontal size={11} style={{ color: 'var(--tdp-faint)' }} />
      </div>
      {DEMO_COMMITS.map((c) => (
        <div key={c.hash} className="tdp-row">
          <span className="tdp-avatar" style={{ width: 28, height: 28, fontSize: 9, background: `${c.color}18`, color: c.color }}>
            {c.initials}
          </span>
          <div className="tdp-row-main">
            <div className="tdp-row-title">{c.msg}</div>
            <div className="tdp-row-sub">
              {c.repo} · <b>{c.hash}</b> · <span style={{ color: T.green }}>{c.delta}</span>
            </div>
          </div>
          <span className="tdp-feed-meta">{c.time}</span>
        </div>
      ))}
    </Card>
  );

  return (
    <div data-theme={isDark ? 'dark' : 'light'} className="tdp" style={{ minHeight: '100%' }}>
      <div className="tdp-wrap">
        {isManager ? (
          /* ═══════════ ADMIN — LIVE OVERVIEW ═══════════ */
          <>
            <div className="tdp-hero">
              <div className="tdp-hero-row">
                <div className="tdp-hero-main">
                  <div className="tdp-eyebrow">
                    <span className="tdp-live-dot" />
                    Work clock · live overview
                  </div>
                  <h1 className="tdp-title">{greeting}, {firstName}.</h1>
                  <p className="tdp-sub">
                    <b>{workingCount}</b> members clocked in · <b>{projects.length}</b> active projects
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'right' }}>
                    <div className="tdp-kpi-label">Budget remaining</div>
                    <div className="tdp-mono" style={{ fontSize: 18, fontWeight: 700, color: T.green }}>
                      ₹{(budget?.remaining ?? 32500).toLocaleString()}
                    </div>
                    <div className="tdp-feed-meta">of ₹{(budget?.total ?? 50000).toLocaleString()}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="tdp-btn tdp-btn-ghost" onClick={() => setActivePage('wcshifts')}>
                      Assign shifts <ArrowRight size={12} />
                    </button>
                    <button className="tdp-btn tdp-btn-primary" onClick={() => setActivePage('wcbudget')}>
                      Budget live <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="tdp-kpi-grid">
              {[
                { label: 'Active now', value: String(workingCount), sub: `of ${isReal ? approvedMembers.length : 4} members`, color: T.green, icon: Users },
                { label: 'Deducted today', value: '₹2,850', sub: 'running', color: T.red, icon: Activity },
                { label: 'Hours logged', value: '5.7', sub: 'today', color: T.accent, icon: Clock3 },
                { label: 'Commits today', value: '8', sub: 'active', color: T.cyan, icon: GitCommitHorizontal },
              ].map((kpi, i) => (
                <Card key={kpi.label} delay={0.06 + i * 0.05} className="tdp-kpi">
                  <span className="tdp-kpi-icon" style={{ background: `${kpi.color}18`, color: kpi.color }}>
                    <kpi.icon size={14} />
                  </span>
                  <div className="tdp-kpi-value">{kpi.value}</div>
                  <div className="tdp-kpi-label">{kpi.label}</div>
                  <div className="tdp-kpi-sub" style={{ color: kpi.color }}>{kpi.sub}</div>
                </Card>
              ))}
            </div>

            <div className="tdp-section">{PomodoroCard}</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 18 }} className="tdp-wc-grid">
              <style>{`@media (min-width: 1080px) { .tdp-wc-grid { grid-template-columns: minmax(0,1fr) minmax(0,1fr) !important; } }`}</style>
              <Card delay={0.22}>
                <div className="tdp-section-title" style={{ padding: '14px 16px 10px', margin: 0, borderBottom: '1px solid var(--tdp-border)' }}>
                  <span>Live member status</span>
                  <span className="tdp-pill" style={{ background: `${T.green}1a`, color: T.green }}>● live</span>
                </div>
                {(isReal ? approvedMembers : []).length === 0 && isReal && (
                  <div className="tdp-feed-meta" style={{ padding: 24, textAlign: 'center' }}>
                    No members clocked in yet. Invite your team to get started.
                  </div>
                )}
                {(isReal ? approvedMembers : [
                  { id: 'd1', initials: 'AS', name: 'Aniket Sharma', role: 'owner', status: 'working' as const, hoursThisWeek: 12.5, rate: 500 },
                  { id: 'd2', initials: 'SR', name: 'Sneha Rao', role: 'lead', status: 'working' as const, hoursThisWeek: 18, rate: 400 },
                  { id: 'd3', initials: 'RM', name: 'Rahul Mehta', role: 'dev', status: 'away' as const, hoursThisWeek: 8, rate: 300 },
                ]).map((m) => (
                  <div key={m.id} className="tdp-row">
                    <span className="tdp-avatar" style={{ width: 32, height: 32, fontSize: 10, background: `linear-gradient(135deg, ${T.accent}, ${T.accent2})`, position: 'relative' }}>
                      {m.initials}
                      {m.status === 'working' && (
                        <span style={{ position: 'absolute', bottom: -1, right: -1, width: 9, height: 9, borderRadius: 99, background: T.green, border: '2px solid var(--tdp-solid)' }} />
                      )}
                    </span>
                    <div className="tdp-row-main">
                      <div className="tdp-row-title">{m.name}</div>
                      <div className="tdp-row-sub" style={{ textTransform: 'capitalize' }}>{m.role}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div className="tdp-mono" style={{ fontSize: 11, fontWeight: 700, color: m.status === 'working' ? T.green : 'var(--tdp-faint)' }}>
                        {m.status === 'working' ? `${m.hoursThisWeek} hrs` : m.status}
                      </div>
                      <div className="tdp-feed-meta">₹{m.rate}/hr</div>
                    </div>
                  </div>
                ))}
              </Card>
              <CommitsCard title="Latest commits" delay={0.26} />
            </div>
          </>
        ) : (
          /* ═══════════ DEVELOPER — MY CLOCK ═══════════ */
          <>
            <div className="tdp-hero">
              <div className="tdp-timer-hero">
                <div style={{ minWidth: 220 }}>
                  <div className="tdp-timer-status" style={{ color: working ? T.green : T.red }}>
                    <span className={`tdp-timer-dot ${working ? 'tdp-timer-dot-on' : 'tdp-timer-dot-off'}`} />
                    {working ? 'Shift active' : 'Offline'}
                  </div>
                  <div className="tdp-timer">{fmtClock(elapsed)}</div>
                  <div className="tdp-timer-date">{today}</div>
                </div>

                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <div className="tdp-earned-chip">
                    <div className="tdp-kpi-label">Earned today</div>
                    <div className="tdp-earned-val">₹{earned}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <motion.button
                      whileHover={{ scale: 1.06 }}
                      whileTap={{ scale: 0.94 }}
                      className="tdp-session-btn"
                      style={{ background: working ? `linear-gradient(135deg, ${T.red}, #e0344e)` : `linear-gradient(135deg, ${T.green}, #0da271)` }}
                      onClick={() => {
                        setWorking(!working);
                        showToast(working ? 'Work ended' : 'Work started', working ? `${fmtClock(elapsed)} logged` : 'Timer running', working ? 'info' : 'success');
                      }}
                      aria-label={working ? 'Stop session' : 'Start session'}
                    >
                      {working ? <Pause size={20} /> : <Play size={20} style={{ marginLeft: 2 }} />}
                    </motion.button>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tdp-text)' }}>
                        {working ? 'Stop Session' : 'Start Session'}
                      </div>
                      <div className="tdp-feed-meta">Rate: ₹{myRate}/hr</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="tdp-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))' }}>
              {[
                { label: 'Earned today', value: `₹${earned}`, sub: `₹${myRate}/hr active`, color: T.green, icon: Zap },
                { label: 'Hours today', value: (elapsed / 3600).toFixed(1), sub: 'of 8 assigned', color: T.accent, icon: Clock3 },
                { label: 'Commits today', value: '3', sub: 'active', color: T.cyan, icon: GitCommitHorizontal },
              ].map((kpi, i) => (
                <Card key={kpi.label} delay={0.06 + i * 0.05} className="tdp-kpi">
                  <span className="tdp-kpi-icon" style={{ background: `${kpi.color}18`, color: kpi.color }}>
                    <kpi.icon size={14} />
                  </span>
                  <div className="tdp-kpi-value">{kpi.value}</div>
                  <div className="tdp-kpi-label">{kpi.label}</div>
                  <div className="tdp-kpi-sub" style={{ color: kpi.color }}>{kpi.sub}</div>
                </Card>
              ))}
            </div>

            <div className="tdp-section">{PomodoroCard}</div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 18 }} className="tdp-wc-grid">
              <style>{`@media (min-width: 1080px) { .tdp-wc-grid { grid-template-columns: minmax(0,1fr) minmax(0,1fr) !important; } }`}</style>
              <Card delay={0.22}>
                <div className="tdp-section-title" style={{ padding: '14px 16px 10px', margin: 0, borderBottom: '1px solid var(--tdp-border)' }}>
                  <span>Today's work log</span>
                  <span className="tdp-pill" style={{ background: working ? `${T.green}1a` : 'var(--tdp-hover)', color: working ? T.green : 'var(--tdp-faint)' }}>
                    {working ? '● working' : '● not started'}
                  </span>
                </div>
                {!working ? (
                  <div className="tdp-feed-meta" style={{ padding: 30, textAlign: 'center' }}>
                    Click Start Session to begin your shift. Time and commits will appear here.
                  </div>
                ) : (
                  <div style={{ padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 12, border: `1px solid ${T.green}33`, background: `${T.green}0d`, padding: '12px 14px' }}>
                      <span className="tdp-mono" style={{ fontSize: 11, fontWeight: 700, color: T.green }}>● Session active</span>
                      <span className="tdp-mono" style={{ fontSize: 11, color: 'var(--tdp-dim)' }}>{fmtClock(elapsed)} logged</span>
                    </div>
                    <div className="tdp-feed-meta" style={{ marginTop: 10 }}>
                      Keep the flow — commits pushed during this session attach to your shift automatically.
                    </div>
                  </div>
                )}
              </Card>
              <CommitsCard title="My commits today" delay={0.26} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
